import { type FC, type SyntheticEvent, useCallback, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import Modal from "~/common/components/Modal";

import useMediaQuery from "~/common/hooks/useMediaQuery";
import { Alert, AlertTitle, Button, Stack, TextField } from "@schaeffler-commerce/design-system";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "@schaeffler-commerce/design-system";
import { useTranslation } from "react-i18next";
import { trackLoginAttempt, trackLoginSuccess } from "~/app/analytics/ga/ga";
import { useLoginApi } from "~/features/auth/login/components/LoginPopup";
import { Link } from "~/common/components/Link";
import { redirectToSrp } from "~/utils/redirectionUtil";

interface Props {
  initialEmail: string;
  onResetClick: (email: string) => void;
  // use to configure an alternative eventAction in GA
  alternativeGAAction?: string;
}

/**
 * Medias business login surface (task 0262 restore): username/password form
 * posting to the OCC credential validator (`loginApi`) — no
 * authorization-server redirect.
 */
const MediasBusinessLoginPopup: FC<Props> = ({ initialEmail, onResetClick, alternativeGAAction }) => {
  const { t } = useTranslation();

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwdRef = useRef<HTMLInputElement>(null);

  const [popupVisible, setPopupVisible] = useState(true);

  const navigate = useNavigate();

  const { login, error, inFlight } = useLoginApi({
    onBeforeLogin: () => trackLoginAttempt(alternativeGAAction),
    onLoginSuccess: () => trackLoginSuccess(alternativeGAAction),
    onLoginCompleted: () => setPopupVisible(false),
  });

  const invalidInputs = error === "login.error.credentials";

  const resetCb = () => {
    const email = emailRef.current?.value ?? "";
    onResetClick(email);
  };

  const doLogin = useCallback(async () => {
    if (!emailRef.current || !passwdRef.current) {
      return;
    }
    await login(emailRef.current.value, passwdRef.current.value);
  }, [login]);

  const resetPassword = (
    <div className={"d-flex flex-justify-between flex-align-center mt-2 mb-3"}>
      <Link onClick={resetCb} data-test-id={"forgot-pw-link"} component="button" type="button">
        {t("page.login.forgottenPwdLink")}
      </Link>
    </div>
  );

  const onSubmit = useCallback(
    (e: SyntheticEvent<unknown>) => {
      e.preventDefault();
      // noinspection JSIgnoredPromiseFromCall
      doLogin();
    },
    [doLogin],
  );

  const onClose = () => {
    setPopupVisible(false);
    // D11 (task 0231): the popup-cancel target routes through the SPA router
    // like its SignoutFromPlusPopUp twin — the former document reload was a
    // missing navigate handoff, not a requirement.
    redirectToSrp(navigate);
  };

  const footer = (
    <Stack direction={"row"} sx={{ gap: 1 }}>
      <Button color={"primary"} variant={"text"} data-test-id={"login-cancel-button"} onClick={onClose}>
        {t("cancel")}
      </Button>
      <Button
        color={"primary"}
        loading={inFlight}
        data-test-id={"login-signin-button"}
        onClick={onSubmit}
        fullWidth={!isDesktop}
      >
        {t("header.link.signIn")}
      </Button>
    </Stack>
  );

  return (
    popupVisible && (
      <Modal onClose={onClose} footerChildren={footer} title={t("header.link.signIn")}>
        <div style={{ paddingBottom: "16px" }}>
          <Alert severity={"info"}>
            <AlertTitle>{t("popup.signout.info.header")}</AlertTitle>
            {t("popup.signin.info.message")}
          </Alert>
        </div>
        <form onSubmit={onSubmit}>
          <button hidden type={"submit"} />
          <div className={"mt-3 mb-2 email-field"}>
            <TextField
              fullWidth
              inputRef={emailRef}
              defaultValue={initialEmail}
              name={"j_username"}
              slotProps={{ htmlInput: { "data-test-id": "login-input" } }}
              label={t("page.login.email")}
              error={invalidInputs}
            />
          </div>
          <div className={"password-field"}>
            <TextField
              fullWidth
              inputRef={passwdRef}
              slotProps={{ htmlInput: { "data-test-id": "password-input" } }}
              name={"j_password"}
              label={t("page.login.password")}
              type={"password"}
              error={invalidInputs}
            />
          </div>
          {error && (
            <Alert className={"my-2"} severity={"error"}>
              {t(error)}
            </Alert>
          )}
          {isDesktop && resetPassword}
        </form>
      </Modal>
    )
  );
};
export default MediasBusinessLoginPopup;
